import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../../../components/Toast/ToastContext';
import MedicalInformation from './FormSections/MedicalInformation';
import EmergencyContact from './FormSections/EmergencyContact';
import ContactDetails from './FormSections/ContactDetails';
import PaymentDetails from './FormSections/PaymentDetails';
import { fetchFormData, saveFormData, submitFormData } from '../../../../../Actions/FormActions';
import { validateForm } from '../../../../../utils/validation';
import { setDialogAction } from '../../../../../Actions/UserActions';
import FormList from './FormList';

const UserForm = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [activeStep, setActiveStep] = useState(() => {
    // Initialize based on viewport width or previously saved step
    const savedStep = sessionStorage.getItem('activeFormStep');
    const isMobile = window.innerWidth < 768;

    // Clear saved step if on mobile
    if (isMobile) {
      sessionStorage.removeItem('activeFormStep');
      return '';
    }

    return savedStep || 'medical';
  }); // medical, emergency, contact, payment

  // Persist active step to session storage
  useEffect(() => {
    if (activeStep) {
      sessionStorage.setItem('activeFormStep', activeStep);
    }
  }, [activeStep]);
  const [errors, setErrors] = useState({});
  const [showInfoBanner, setShowInfoBanner] = useState(() => {
    try {
      const raw = localStorage.getItem('user_data');
      const user = raw ? JSON.parse(raw) : {};
      const dialogs = Array.isArray(user?.dialogs) ? user.dialogs : [];
      const entry = dialogs.find(d => d?.type === 'form_disclaimer');
      return !(entry && (entry.value === true || entry.value === 'true'));
    } catch (e) {
      return true;
    }
  });
  const [formData, setFormData] = useState({
    full_name: JSON.parse(localStorage.getItem('user_data'))?.name,
    age: '',
    gender: '',
    weight: '',
    height: '',
    blood_group: '',
    mobile: JSON.parse(localStorage.getItem('user_data'))?.mobile,
    email: JSON.parse(localStorage.getItem('user_data'))?.email,
    medical_condition: {
      name: '',
      notes: '',
      doctor_name: '',
      allergies: '',
      certificate: ''
    },
    symbols: [],
    disability: {
      has_disability: 'no',
      type: '',
      has_certificate: 'no',
      certificate: ''
    },
    emergency_contacts: [
      {
        name: '',
        mobile: '',
        relationship: '',
        email: ''
      }
    ],
    // Backend keys
    lost_and_found: false,
    consent: false,
    terms_and_conditions: false,
    address: {
      line_1: '',
      line_2: '',
      city: '',
      state: '',
      pincode: ''
    },
    status: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Lock the form only when status is 'rejected'. All other statuses (saved/submitted/verified/paid) remain editable.
  const isLocked = Boolean(formData?.status) && String(formData.status).toLowerCase() === 'rejected';
  const noFormFound = !formData || !formData._id; // treat absence of form id as no form found

  useEffect(() => {
    fetchFormDataFun();
  }, []);

  // No initial fetch; rely on dialogs from login response in localStorage

  // Handle viewport resize across the mobile/desktop breakpoint only.
  // Mobile browser chrome show/hide fires resize without crossing 768px; clearing
  // activeStep on those events kicks the user back to the form list mid-edit.
  useEffect(() => {
    const MOBILE_BREAKPOINT = 768;
    let wasMobile = window.innerWidth < MOBILE_BREAKPOINT;

    const handleResize = () => {
      const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
      if (isMobile === wasMobile) return;
      wasMobile = isMobile;
      if (isMobile) {
        setActiveStep('');
        sessionStorage.removeItem('activeFormStep');
      } else {
        setActiveStep((current) => current || 'medical');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const normalizeEmergencyContacts = (data) => {
    const incoming = data || {};
    const contacts = Array.isArray(incoming.emergency_contacts) ? incoming.emergency_contacts : [];
    const normalizedContacts = contacts.map((c) => ({
      ...c,
      email: c?.email || ''
    }));
    return {
      ...incoming,
      emergency_contacts: normalizedContacts.length > 0 ? normalizedContacts : [
        { name: '', mobile: '', relationship: '', email: '' }
      ]
    };
  };

  const normalizeBackendFlags = (data) => {
    const incoming = data || {};
    return {
      ...incoming,
      lost_and_found: typeof incoming.lost_and_found === 'boolean' ? incoming.lost_and_found : Boolean(incoming.lost_n_found),
      consent: Boolean(incoming.consent),
      terms_and_conditions: Boolean(incoming.terms_and_conditions)
    };
  };

  const scrollAndFocusById = (elementId) => {
    if (!elementId) return;
    const tryFocus = (attempts = 0) => {
      const el = document.getElementById(elementId);
      if (el) {
        try {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (typeof el.focus === 'function') {
            el.focus({ preventScroll: true });
          }
        } catch (_) { /* no-op */ }
        return;
      }
      if (attempts < 10) {
        setTimeout(() => tryFocus(attempts + 1), 80);
      }
    };
    // small delay to allow the target step to render
    setTimeout(() => tryFocus(0), 120);
  };

  const findFirstInvalidTarget = (medicalErrors, emergencyErrors, contactErrors) => {
    // Medical step mapping in priority order
    const medicalMap = [
      ['full_name', 'full_name'],
      ['age', 'age'],
      ['gender', 'gender'],
      ['weight', 'weight'],
      ['height', 'height'],
      ['blood_group', 'blood_group'],
      ['medical_certificate', 'medical_certificate'],
      ['medical_condition_name', 'medical_condition_name'],
      ['medical_condition_notes', 'medical_condition_notes'],
      ['medical_condition_doctor', 'medical_condition_doctor'],
      ['has_disability', 'disability_has_disability_yes'],
      ['disability_type', 'disability_type_visible'],
      ['has_certificate', 'disability_has_certificate_yes'],
      ['disability_certificate', 'disability_certificate'],
      ['symbols', 'symbols'],
      ['consent', 'consent_checkbox'],
      ['terms_and_conditions', 'terms_and_conditions_checkbox']
    ];
    for (const [key, id] of medicalMap) {
      if (medicalErrors && medicalErrors[key]) {
        return { step: 'medical', id, message: medicalErrors[key] };
      }
    }

    // Emergency contacts - check first contact, then second
    const ecArray = Array.isArray(emergencyErrors) ? emergencyErrors : [];
    for (let i = 0; i < ecArray.length; i++) {
      const ec = ecArray[i] || {};
      const order = ['name', 'relationship', 'mobile', 'email'];
      for (const field of order) {
        if (ec && ec[field]) {
          const id = `ec_${i}_${field === 'relationship' ? 'relationship' : field}`;
          return { step: 'emergency', id, message: ec[field] };
        }
      }
    }

    // Contact details
    const contactMap = [
      ['address.pincode', 'address_pincode'],
      ['address.city', 'address_city'],
      ['address.state', 'address_state'],
      ['address.district', 'address_district'],
      ['address.line_1', 'address_line_1'],
      ['mobile', 'contact_mobile'],
      ['email', 'contact_email']
    ];
    for (const [key, id] of contactMap) {
      if (contactErrors && contactErrors[key]) {
        return { step: 'contact', id, message: contactErrors[key] };
      }
    }

    return null;
  };

  // Hoisted function declarations to satisfy eslint no-undef
  async function fetchFormDataFun() {
    try {
      const response = await fetchFormData();
      if (response.data) {
        setFormData(normalizeEmergencyContacts(normalizeBackendFlags(response.data)));
      } else if (response.message === "No forms found") {
        // Initialize with empty form data when no forms are found
        setFormData(normalizeEmergencyContacts({
          full_name: JSON.parse(localStorage.getItem('user_data'))?.name,
          age: '',
          gender: '',
          weight: '',
          height: '',
          blood_group: '',
          mobile: JSON.parse(localStorage.getItem('user_data'))?.mobile,
          email: JSON.parse(localStorage.getItem('user_data'))?.email,
          medical_condition: {
            name: '',
            notes: '',
            doctor_name: '',
            allergies: '',
            certificate: ''
          },
          symbols: [],
          disability: {
            has_disability: 'no',
            type: '',
            has_certificate: 'no',
            certificate: ''
          },
          emergency_contacts: [
            {
              name: '',
              mobile: '',
              relationship: '',
              email: ''
            }
          ],
          lost_and_found: false,
          consent: false,
          terms_and_conditions: false,
          address: {
            line_1: '',
            line_2: '',
            city: '',
            state: '',
            district: '',
            pincode: ''
          },
          status: 'Saved'
        }));
      }
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch form data');
      setLoading(false);
    }
  }

  async function handleCloseBanner() {
    setShowInfoBanner(false);
    try {
      await setDialogAction('form_disclaimer', 'true');
    } catch (_) {
      // ignore post failure; UI state already updated
    }
  }

  const refreshLatestFormData = async () => {
    try {
      const latest = await fetchFormData();
      if (latest?.data) {
        setFormData(normalizeEmergencyContacts(normalizeBackendFlags(latest.data)));
      }
    } catch (e) {
      // Silently ignore refresh errors to avoid disrupting UX
    }
  };

  const handleSave = async (advanceStep = true) => {
    try {
      // Extract files from formData
      const medicalCertificate = formData.medical_condition?.certificate;
      const disabilityCertificate = formData.disability?.certificate;

      // Create a clean copy of formData without File objects
      const cleanFormData = {
        ...formData,
        medical_condition: {
          ...formData.medical_condition,
          certificate: formData.medical_condition?.certificate instanceof File ? '' : formData.medical_condition?.certificate
        },
        disability: {
          ...formData.disability,
          certificate: formData.disability?.certificate instanceof File ? '' : formData.disability?.certificate
        }
      };

      // Save all current form data without validation
      const response = await saveFormData(cleanFormData, medicalCertificate, disabilityCertificate);
      if (response.data) {
        // Preserve the current form data and merge with response
        setFormData(prevData => normalizeEmergencyContacts({
          ...prevData,
          ...response.data,
          // Preserve file objects if they exist
          medical_condition: {
            ...response.data.medical_condition,
            certificate: medicalCertificate instanceof File ? medicalCertificate : response.data.medical_condition?.certificate || ''
          },
          disability: {
            ...response.data.disability,
            certificate: disabilityCertificate instanceof File ? disabilityCertificate : response.data.disability?.certificate || '',
            has_certificate: response.data.disability?.certificate ? 'yes' : 'no'
          }
        }));
        setError(null);

        // Show success message
        showToast('Form section saved successfully!', 'success');

        // Optionally navigate to next section based on current step and scroll to top
        if (advanceStep) {
          if (activeStep === 'medical') {
            setActiveStep('emergency');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else if (activeStep === 'emergency') {
            setActiveStep('contact');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }

        // Refresh latest data from backend to ensure we have server state
        await refreshLatestFormData();
      }
    } catch (err) {
      setError('Failed to save form data');
      showToast('Failed to save form data', 'error');
    }
  };

  const handleSubmit = async () => {
    // Validate all steps before submitting (full validation)
    const medicalErrors = validateForm(formData, 'medical', false);
    const emergencyErrors = validateForm(formData, 'emergency', false);
    const contactErrors = validateForm(formData, 'contact', false);

    // Maintain shape compatibility for inner sections
    const allErrors = { ...medicalErrors, ...contactErrors };
    // Emergency errors is an array; attach under a unique symbol-less key for setErrors usage in children
    // but keep original for EmergencyContact via numeric indexing
    emergencyErrors.forEach((err, idx) => {
      if (err && Object.keys(err).length > 0) allErrors[idx] = err;
    });
    setErrors(allErrors);

    // If any errors, navigate to the first invalid field
    const firstTarget = findFirstInvalidTarget(medicalErrors, emergencyErrors, contactErrors);
    if (firstTarget) {
      setActiveStep(firstTarget.step);
      scrollAndFocusById(firstTarget.id);
      showToast(firstTarget.message, 'error');
      return;
    }

    try {
      // Extract files from formData
      const medicalCertificate = formData.medical_condition?.certificate;
      const disabilityCertificate = formData.disability?.certificate;

      // Create a clean copy of formData without File objects
      const cleanFormData = {
        ...formData,
        medical_condition: {
          ...formData.medical_condition,
          certificate: formData.medical_condition?.certificate instanceof File ? '' : formData.medical_condition?.certificate
        },
        disability: {
          ...formData.disability,
          certificate: formData.disability?.certificate instanceof File ? '' : formData.disability?.certificate
        }
      };

      const response = await submitFormData(cleanFormData, medicalCertificate, disabilityCertificate);
      if (response.data) {
        // Preserve the current form data and merge with response
        setFormData(prevData => normalizeEmergencyContacts({
          ...prevData,
          ...response.data,
          // Preserve file objects if they exist
          medical_condition: {
            ...response.data.medical_condition,
            certificate: medicalCertificate instanceof File ? medicalCertificate : response.data.medical_condition?.certificate || ''
          },
          disability: {
            ...response.data.disability,
            certificate: disabilityCertificate instanceof File ? disabilityCertificate : response.data.disability?.certificate || '',
            has_certificate: response.data.disability?.certificate ? 'yes' : 'no'
          }
        }));
        setError(null);

        // Show success message
        showToast('Form submitted successfully!', 'success');

        // Only navigate to payment if form is verified
        if (response.data.status === 'Verified') {
          setActiveStep('payment');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        // Only navigate back to form list if form is not verified
        if (response.data.status !== 'Verified') {
          setActiveStep('medical');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }

        // Refresh latest data from backend to ensure we have server state
        await refreshLatestFormData();
      }
    } catch (err) {
      setError('Failed to submit form data');
      // Show specific error message if available from backend
      const errorMessage = err.response?.data?.message || 'Failed to submit form data';
      showToast(errorMessage, 'error');

      // If medical certificate is missing, highlight the error
      if (errorMessage.toLowerCase().includes('medical') && errorMessage.toLowerCase().includes('certificate')) {
        setErrors(prev => ({
          ...prev,
          medical_certificate: 'Medical Prescription is required'
        }));
        setActiveStep('medical'); // Take user back to medical section
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handlePay = async () => {
    // Only allow payment if form is not in Saved state
    if (formData.status !== 'Saved') {
      setActiveStep('payment');
    } else {
      showToast('Form must be verified before proceeding to payment', 'error');
    }
  };

  const renderStepIndicator = () => {
    return (
      <div className="hidden md:block sticky top-0 z-[5] bg-[#F3EDF9] pt-1">
        <div className="grid grid-cols-12 px-8 mb-8 border-b border-[#753ABF]">
          {showInfoBanner && (
            <div className="col-span-12 rounded-lg border m-6 px-6 py-4 bg-[#D8FDFF] text-[#753ABF] relative flex items-center justify-between  border-[#753ABF]">
              <p>
                Please provide the information and complete the steps to get access to your card
              </p>
              <button
                className="ml-2 bg-[#FFFFFF] p-1 rounded-full"
                onClick={handleCloseBanner}
              >
                <img src="/Assets/Images/crossIcon.svg" className='h-5 w-5' alt="Close information banner" />
              </button>
            </div>
          )}
          {/* Step Indicators for Desktop */}
          <div className={`col-span-12 lg:col-span-8 flex items-center px-4 gap-x-6 flex-wrap lg:flex-nowrap ${showInfoBanner ? 'pt-0' : 'pt-12'}`}>
            <div
              className={`flex-1 text-center cursor-pointer relative leading-[3.6rem] ${activeStep === 'medical'
                ? 'text-[#753ABF] font-semibold after:content-[""] after:absolute after:left-0 after:bottom-[0px] after:w-full after:h-[2px] after:bg-[#753ABF]'
                : 'text-[#666666]'
                }`}
              onClick={() => setActiveStep('medical')}
            >
              Medical Information
            </div>
            <div
              className={`flex-1 text-center cursor-pointer relative leading-[3.6rem] ${activeStep === 'emergency'
                ? 'text-[#753ABF] font-semibold after:content-[""] after:absolute after:left-0 after:bottom-[0px] after:w-full after:h-[2px] after:bg-[#753ABF]'
                : 'text-[#666666]'
                }`}
              onClick={() => setActiveStep('emergency')}
            >
              Emergency Contact
            </div>
            <div
              className={`flex-1 text-center cursor-pointer relative leading-[3.6rem] ${activeStep === 'contact'
                ? 'text-[#753ABF] font-semibold after:content-[""] after:absolute after:left-0 after:bottom-[0px] after:w-full after:h-[2px] after:bg-[#753ABF]'
                : 'text-[#666666]'
                }`}
              onClick={() => setActiveStep('contact')}
            >
              Contact Details
            </div>
            <div
              className={`flex-1 text-center relative leading-[3.6rem] ${((formData.status === 'Saved') || noFormFound) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                } ${activeStep === 'payment'
                  ? 'text-[#753ABF] font-semibold after:content-[""] after:absolute after:left-0 after:bottom-[0px] after:w-full after:h-[2px] after:bg-[#753ABF]'
                  : 'text-[#666666]'
                }`}
              onClick={() => !((formData.status === 'Saved') || noFormFound) && setActiveStep('payment')}
              title={(formData.status === 'Saved') ? 'Form must be verified before accessing payment details' : (noFormFound ? 'Create and save your form before accessing payment details' : '')}
            >
              Payment Details
            </div>
          </div>
          <div className={`col-span-12 lg:col-span-4 flex justify-end px-4 py-2 ${showInfoBanner ? 'pt-0' : 'pt-12'}`}>
            <div className="flex flex-nowrap gap-3 justify-end">
              {activeStep !== 'payment' && (
                    <>
                      <button
                        onClick={() => { if (!isLocked) handleSave(false); }}
                        disabled={isLocked}
                        className={`px-6 py-2 h-fit rounded-md  min-w-[8rem] ${activeStep === 'contact' ? isLocked ? 'bg-[#E0D2EE]/40 text-[#753ABF]/40 cursor-not-allowed' : 'bg-[#E0D2EE] text-[#753ABF] hover:bg-[#E0D2EE]/90' : isLocked ? 'font-bold bg-[#753ABF]/40 text-[#FFFFFF] cursor-not-allowed' : 'font-bold bg-[#753ABF] text-[#FFFFFF] chover:bg-[#753ABF]/90'}`}
                        title={isLocked ? 'Editing is disabled for this form status' : ''}
                      >
                        Save
                      </button>
                      <button
                        onClick={() => { if (!isLocked) { (activeStep === 'contact' ? handleSubmit() : handleSave()); } }}
                        disabled={isLocked}
                        className={`px-6 py-2 h-fit rounded-md  min-w-[8rem] ${activeStep === 'contact' ? isLocked ? 'font-bold bg-[#753ABF]/40 text-[#FFFFFF] cursor-not-allowed' : 'font-bold bg-[#753ABF] text-[#FFFFFF] chover:bg-[#753ABF]/90' : isLocked ? 'bg-[#E0D2EE]/40 text-[#753ABF]/40 cursor-not-allowed' : 'bg-[#E0D2EE] text-[#753ABF] hover:bg-[#E0D2EE]/90'}`}
                        title={isLocked ? 'Editing is disabled for this form status' : ''}
                      >
                        {activeStep === 'contact' ? 'Submit' : 'Continue'}
                      </button>
                    </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  return (
    <div className="mx-auto bg-[#F3EDF9] rounded-lg shadow-sm pb-6 min-h-screen">
      {!activeStep && <div className="md:hidden bg-[#F3EDF9] rounded-lg py-2 mb-6">
        {showInfoBanner && (
          <div className="rounded-lg border m-6 p-6 bg-[#D8FDFF] text-[#753ABF] relative flex items-center justify-between  border-[#753ABF]">
            <p>
              Please provide the information and complete the steps to get access to your card
            </p>
            <button
              className="ml-2 bg-[#FFFFFF] md:hidden p-1 rounded-full"
              onClick={handleCloseBanner}
            >
              <img src="/Assets/Images/crossIcon.svg" className='h-5 w-5' alt="Close information banner" />
            </button>
          </div>
        )}
        {<FormList activeStep={activeStep} setActiveStep={setActiveStep} formData={formData} />}
      </div>}

      {/* Mobile Step Indicator */}
      <div className="md:hidden px-6 pt-4 mb-6 grid grid-cols-12">
        <h2 className="col-span-6 text-2xl font-bold text-[#753ABF] break-words">
          {activeStep === 'medical' && 'Medical Information'}
          {activeStep === 'emergency' && 'Emergency Contact'}
          {activeStep === 'contact' && 'Contact Details'}
          {activeStep === 'payment' && 'Payment Details'}
        </h2>
        {/* Mobile navigation buttons */}
        {activeStep && <div className="col-span-6 grid gap-3">
          <button
            onClick={() => setActiveStep('')}
            className="col-span-6 w-full sm:w-auto px-6 py-2 bg-[#E0D2EE] text-[#753ABF] rounded-md min-w-[8rem] hover:bg-[#E0D2EE]/90"
          >
            Back
          </button>
          {activeStep !== 'payment' && (
                <button
                  onClick={() => { if (!isLocked) { (activeStep === 'contact' ? handleSubmit() : handleSave()); } }}
                  disabled={isLocked}
                  className={`col-span-6 w-full sm:w-auto px-6 py-2 rounded-md min-w-[8rem] font-bold ${isLocked ? 'bg-[#753ABF]/60 text-[#FFFFFF] cursor-not-allowed' : (activeStep === 'medical'
                    ? 'bg-[#753ABF]/90 text-[#FFFFFF]'
                    : 'bg-[#753ABF] text-[#FFFFFF] hover:bg-[#753ABF]/90')}
                    `}
                  title={isLocked ? 'Editing is disabled for this form status' : ''}
                >
                  {activeStep === 'contact' ? 'Submit' : 'Save'}
                </button>
          )}
        </div>}
      </div>

      {renderStepIndicator()}

      <form className="space-y-6 px-6 pb-6 w-100 md:w-[65vw] w-full">
        {activeStep === 'medical' && (
          <MedicalInformation formData={formData} setFormData={setFormData} errors={errors} setErrors={setErrors} isLocked={isLocked} formStatus={formData?.status} />
        )}
        {activeStep === 'emergency' && (
          <EmergencyContact formData={formData} setFormData={setFormData} errors={errors} setErrors={setErrors} isLocked={isLocked} />
        )}
        {activeStep === 'contact' && (
          <ContactDetails formData={formData} setFormData={setFormData} errors={errors} setErrors={setErrors} isLocked={isLocked} />
        )}
        {activeStep === 'payment' && (
          <PaymentDetails formData={formData} setFormData={setFormData} formStatus={formData?.status} />
        )}
      </form>
    </div>
  );
};

export default UserForm;
